import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { opsAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import { useAuth } from "../contexts/AuthContext";
import {
  Users, ShoppingBag, Phone, BarChart3, Flame, ArrowRight,
  Search, Loader2, Shield, ArrowLeft, Crown, LogOut
} from "lucide-react";

function StatCard({ label, value, icon: Icon, color, bg }) {
  return (
    <Card className="border-slate-100">
      <CardContent className="p-5">
        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
          <Icon className={`w-[18px] h-[18px] ${color}`} strokeWidth={2} />
        </div>
        <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
        <p className="text-[10px] text-slate-400 mt-0.5 font-medium uppercase tracking-wider">{label}</p>
      </CardContent>
    </Card>
  );
}

export default function OpsPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const { logout } = useAuth();
  const navigate = useNavigate();

  // noindex for /ops
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "robots"; document.head.appendChild(meta); }
    meta.content = "noindex, nofollow";
    return () => { if (meta) meta.content = "index, follow"; };
  }, []);

  const fetchData = async (searchQuery = "") => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.all([
        opsAPI.stats(),
        opsAPI.listUsers({ search: searchQuery, limit: 50 }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setTotal(usersRes.data.total);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData(search);
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb]" data-testid="ops-page">
      {/* Ops Header */}
      <header className="sticky top-0 z-50 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/zest-logo.png" alt="Zest" className="h-8 w-auto" />
            <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-[10px] ml-1">
              <Shield className="w-2.5 h-2.5 mr-1" /> OPS
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white text-xs h-8" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-3 h-3 mr-1" /> Back to App
            </Button>
            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white text-xs h-8" onClick={() => { logout(); navigate("/login"); }}>
              <LogOut className="w-3 h-3 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="Total Users" value={stats.total_users} icon={Users} color="text-blue-600" bg="bg-blue-50" />
            <StatCard label="Restaurants" value={stats.total_restaurants} icon={BarChart3} color="text-emerald-600" bg="bg-emerald-50" />
            <StatCard label="Active AI" value={stats.active_restaurants} icon={Shield} color="text-violet-600" bg="bg-violet-50" />
            <StatCard label="Total Calls" value={stats.total_calls} icon={Phone} color="text-amber-600" bg="bg-amber-50" />
            <StatCard label="Total Orders" value={stats.total_orders} icon={ShoppingBag} color="text-teal-600" bg="bg-teal-50" />
            <StatCard label="Comp Users" value={stats.billing?.comp || 0} icon={Crown} color="text-orange-600" bg="bg-orange-50" />
          </div>
        )}

        {/* User Search */}
        <Card className="border-slate-100">
          <CardHeader className="pb-3 px-5 pt-5 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Users ({total})
            </CardTitle>
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 w-56 text-sm" data-testid="ops-user-search" />
              </div>
              <Button size="sm" type="submit" className="h-9 text-xs">Search</Button>
            </form>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-100 hover:bg-transparent">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold pl-5">User</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Role</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Restaurant</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Plan</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Status</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold text-right pr-5"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(u => {
                      const plan = u.billing?.plan || "none";
                      const status = u.status || "active";
                      const billingStatus = u.billing?.status || "none";
                      return (
                        <TableRow key={u.id} className="cursor-pointer table-row-hover border-slate-50" onClick={() => navigate(`/ops/users/${u.id}`)} data-testid={`ops-user-${u.id}`}>
                          <TableCell className="pl-5 py-3">
                            <div>
                              <p className="text-[13px] font-medium text-slate-800">{u.name}</p>
                              <p className="text-[11px] text-slate-400">{u.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`badge-pill text-[10px] ${u.role === "super_admin" ? "bg-red-50 text-red-600 border-red-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                              {u.role}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-[13px] text-slate-600">{u.restaurant?.business_name || "—"}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`badge-pill text-[10px] ${
                              plan === "premium" ? "bg-orange-50 text-orange-600 border-orange-200"
                              : plan === "starter" ? "bg-blue-50 text-blue-600 border-blue-200"
                              : "bg-slate-50 text-slate-400 border-slate-200"
                            }`}>{plan}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`badge-pill text-[10px] ${
                              billingStatus === "comp" ? "bg-amber-50 text-amber-600 border-amber-200"
                              : billingStatus === "active" ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                              : status === "suspended" ? "bg-red-50 text-red-600 border-red-200"
                              : "bg-slate-50 text-slate-400 border-slate-200"
                            }`}>{billingStatus === "comp" ? "comp" : status}</Badge>
                          </TableCell>
                          <TableCell className="text-right pr-5">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-primary">
                              <ArrowRight className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
